import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, ChevronUpIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { ConversationTranscript } from '@/pages/conversations/components/ConversationTranscript'
import type { Message } from '@/types/conversation'

interface ConversationViewerProps {
  messages: Message[] | null
  toolCalls?: Record<string, unknown>[] | null
  postProcessorToolCalls?: Record<string, unknown>[] | null
  className?: string
}

/**
 * Component for displaying conversation messages from a simulation run.
 * Reuses ConversationTranscript for the messages display.
 */
export function ConversationViewer({
  messages,
  toolCalls,
  postProcessorToolCalls,
  className,
}: ConversationViewerProps) {
  const { t } = useTranslation()
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    messages: true,
    toolCalls: false,
    postProcessorCalls: false,
  })

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const hasToolCalls = toolCalls && toolCalls.length > 0
  const hasPostProcessorCalls = postProcessorToolCalls && postProcessorToolCalls.length > 0

  return (
    <div className={cn('space-y-4', className)}>
      {/* Messages Section - using shared ConversationTranscript */}
      <CollapsibleSection
        title={t('pages.simulations.run_details.messages')}
        count={messages?.length ?? 0}
        isExpanded={expandedSections.messages}
        onToggle={() => toggleSection('messages')}
      >
        {messages && messages.length > 0 ? (
          <ConversationTranscript messages={messages} />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('pages.simulations.run_details.no_messages')}
          </p>
        )}
      </CollapsibleSection>

      {/* Tool Calls Section */}
      {hasToolCalls && (
        <CollapsibleSection
          title={t('pages.simulations.run_details.tool_calls')}
          count={toolCalls.length}
          isExpanded={expandedSections.toolCalls}
          onToggle={() => toggleSection('toolCalls')}
        >
          <pre className="overflow-x-auto rounded-lg bg-gray-50 p-4 text-xs dark:bg-gray-800/50">
            {JSON.stringify(toolCalls, null, 2)}
          </pre>
        </CollapsibleSection>
      )}

      {/* Post-Processor Tool Calls Section */}
      {hasPostProcessorCalls && (
        <CollapsibleSection
          title={t('pages.simulations.run_details.post_processor_calls')}
          count={postProcessorToolCalls.length}
          isExpanded={expandedSections.postProcessorCalls}
          onToggle={() => toggleSection('postProcessorCalls')}
        >
          <pre className="overflow-x-auto rounded-lg bg-gray-50 p-4 text-xs dark:bg-gray-800/50">
            {JSON.stringify(postProcessorToolCalls, null, 2)}
          </pre>
        </CollapsibleSection>
      )}
    </div>
  )
}

interface CollapsibleSectionProps {
  title: string
  count?: number
  isExpanded: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CollapsibleSection({
  title,
  count,
  isExpanded,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
      >
        <span className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {title}
          </span>
          {count !== undefined && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              {count}
            </span>
          )}
        </span>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isExpanded && <div className="border-t border-gray-200 p-4 dark:border-gray-700">{children}</div>}
    </div>
  )
}

export default ConversationViewer
